import type { IStrategyRegistry } from './ports';
import { isResolvedTuningConfiguration, isTuningCapabilities, isTuningPatch, TUNING_STRATEGY_IDS } from './settings';
import type { ResolvedTuningConfiguration, TuningCapabilities, TuningPatch, TuningSettingKey, TuningSettingSupport, TuningValue } from './settings';

export interface ITuningDigest { sha256(bytes: Uint8Array): Promise<Uint8Array>; }
const presets: Readonly<Record<string, TuningPatch>> = Object.freeze({
  production: {},
  'required-keyframes': { 'recovery.keyframePolicy': 'required-only' },
  'loss-repair': { 'recovery.keyframePolicy': 'loss-repair', 'recovery.lossKeyframeMinIntervalMs': 3_000 },
  'minimal-instrumentation': { 'telemetry.encodedFrameTap': false, 'telemetry.windowMs': 1_000, 'telemetry.statsIntervalMs': 1_000 },
});

class WebCryptoTuningDigest implements ITuningDigest {
  async sha256(bytes: Uint8Array): Promise<Uint8Array> {
    if (!globalThis.crypto?.subtle) throw new Error('SHA-256 digest unavailable');
    const copy = new Uint8Array(bytes);
    return new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', copy));
  }
}

/** Stable JSON is shared with the receiving endpoint's configuration verification. */
export function canonicalTuningConfigurationJson(configuration: Omit<ResolvedTuningConfiguration, 'configurationHash'>): string {
  const settings = Object.fromEntries(Object.entries(configuration.settings).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0));
  return JSON.stringify({ policyVersion: configuration.policyVersion, settings,
    strategyId: configuration.strategyId, version: configuration.version });
}

export async function hashTuningConfiguration(configuration: Omit<ResolvedTuningConfiguration, 'configurationHash'>,
  digest: ITuningDigest = new WebCryptoTuningDigest()): Promise<string> {
  const bytes = await digest.sha256(new TextEncoder().encode(canonicalTuningConfigurationJson(configuration)));
  if (bytes.length !== 32) throw new Error('SHA-256 dependency returned an invalid digest');
  return `sha256:${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
}

function allows(support: TuningSettingSupport | undefined, value: TuningValue): boolean {
  if (!support || support.mode === 'unsupported' || support.mode === 'host-restart' || support.nativeApi === 'unsupported') return false;
  if (support.values && !support.values.includes(value)) return false;
  return typeof value !== 'number' ||
    ((support.min === undefined || value >= support.min) && (support.max === undefined || value <= support.max));
}

/** Pure immutable preset expansion; native application and session authorization belong to endpoints. */
export class StrategyRegistry implements IStrategyRegistry {
  constructor(private readonly digest: ITuningDigest = new WebCryptoTuningDigest()) {}

  async resolve(production: ResolvedTuningConfiguration, strategyId: string, patch: TuningPatch,
    endpoints: readonly TuningCapabilities[]): Promise<ResolvedTuningConfiguration> {
    if (!isResolvedTuningConfiguration(production) || !isTuningPatch(patch) || !TUNING_STRATEGY_IDS.includes(strategyId)) {
      throw new Error('Invalid tuning strategy configuration');
    }
    if (endpoints.length === 0 || endpoints.some((endpoint) => !isTuningCapabilities(endpoint))) {
      throw new Error('Tuning endpoint capabilities unavailable');
    }
    const overrides: TuningPatch = { ...presets[strategyId], ...patch };
    for (const [key, value] of Object.entries(overrides)) {
      const supported = endpoints.some((endpoint) => endpoint.strategies.includes(strategyId) &&
        allows(endpoint.settings[key as TuningSettingKey], value));
      if (!supported) throw new Error(`Unsupported tuning setting: ${key}`);
    }
    const configuration = { version: 1 as const, policyVersion: production.policyVersion,
      strategyId, settings: Object.freeze({ ...production.settings, ...overrides }) };
    return Object.freeze({ ...configuration, configurationHash: await hashTuningConfiguration(configuration, this.digest) });
  }
}
